import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const mainNav = [
  { label: 'Was wir tun', path: '/was-wir-tun' },
  { label: 'Fördern', path: '/foerdern' },
  { label: 'Selbsthilfe', path: '/selbsthilfe' },
  { label: 'Wissen', path: '/wissen' },
  { label: 'Über uns', path: '/ueber-uns' },
];

export function HeaderNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-white/97 backdrop-blur-xl border-b border-gray-200/60"
      role="banner"
      data-testid="header-nav"
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            data-testid="logo-link"
          >
            <img 
              src="/images/brand/ask-mi-logo-clean.webp" 
              alt="asK-mi Logo" 
              className="h-[52px] w-auto"
            />
            <span 
              className="text-[22px] font-semibold text-gray-900 tracking-tight hidden sm:inline"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              asK-mi
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Hauptnavigation">
            {mainNav.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'px-5 py-2 text-[15px] font-medium transition-colors',
                  isActive(item.path)
                    ? 'text-[#A8923F]'
                    : 'text-gray-600 hover:text-[#A8923F]'
                )}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center">
            <Link to="/foerderer-werden" data-testid="nav-foerderer-cta">
              <Button 
                className="bg-gray-900 hover:bg-gray-800 text-white rounded-md px-5 py-2.5 text-[14px] font-medium"
              >
                Förderer werden
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className="lg:hidden border-t border-gray-200 bg-white"
          data-testid="mobile-menu"
        >
          <nav className="max-w-[1200px] mx-auto px-4 py-6 space-y-1" aria-label="Mobile Navigation">
            {mainNav.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'block px-4 py-3 rounded-xl text-[15px] font-medium transition-colors',
                  isActive(item.path)
                    ? 'bg-[#C4A962]/10 text-[#A8923F]'
                    : 'text-gray-700 hover:bg-gray-50'
                )}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${item.path.slice(1)}`}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-4 mt-4 border-t border-gray-200">
              <Link
                to="/foerderer-werden"
                className="block w-full text-center bg-gray-900 text-white py-3 rounded-lg font-medium"
                onClick={() => setMobileOpen(false)}
              >
                Förderer werden
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
